import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useReward } from '../contexts/RewardContext';
import { useTheme } from '../contexts/ThemeContext';
import { getUserProgress, isItemUnlocked, getUserRewardItems, saveSelectedScene } from '../services/rewardItemsService';

const WritingTools = ({ 
  className = ''
}) => {
  const { user } = useAuth();
  const { rewardItems: cachedRewardItems } = useReward();
  const { themeColors } = useTheme();
  const sceneContainerRef = useRef(null);
  
  // Track sidebar width by actually measuring the sidebar element
  const [sidebarLeft, setSidebarLeft] = useState('256px'); // Default to expanded
  
  // Video backgrounds state - loaded from Firestore
  const [videoBackgrounds, setVideoBackgrounds] = useState([]);
  const [availableVideos, setAvailableVideos] = useState({}); // Map of video name to {id, hash, unlocked}
  const [videoOptions, setVideoOptions] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [loadedVideos, setLoadedVideos] = useState(new Set()); // Track which videos have been loaded
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  /*
   * Respect the OS-level "Reduce Motion" preference (WCAG 2.3.3 / prefers-reduced-motion).
   * When true we skip rendering the autoplay Vimeo iframe so no motion plays automatically.
   */
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sceneIconRef = useRef(null);
  const sceneDropdownRef = useRef(null);

  // Measure actual sidebar width instead of hardcoding
  useEffect(() => {
    const measureSidebar = () => {
      // Find the sidebar element (it has fixed positioning and is on the left)
      // Try multiple selectors to find it reliably
      const sidebar = document.querySelector('aside.fixed, nav.fixed[class*="left-0"], [class*="w-16"][class*="fixed"], [class*="w-64"][class*="fixed"]') ||
                     document.querySelector('[class*="fixed"][class*="left-0"]');
      
      if (sidebar) {
        const rect = sidebar.getBoundingClientRect();
        const measuredWidth = rect.width;
        setSidebarLeft(`${measuredWidth}px`);
      } else {
        // Fallback to localStorage if sidebar not found yet
        const saved = localStorage.getItem('sidebarCollapsed');
        const isCollapsed = saved ? JSON.parse(saved) : false;
        const fallbackWidth = isCollapsed ? '64px' : '256px';
        setSidebarLeft(fallbackWidth);
      }
    };

    // Measure on mount and when sidebar state changes
    // Use a small delay to ensure DOM is ready
    const timeoutId = setTimeout(measureSidebar, 100);
    
    const handleSidebarStateChange = () => {
      // Delay to let sidebar transition complete (300ms transition + buffer)
      setTimeout(measureSidebar, 350);
    };

    window.addEventListener('sidebarStateChanged', handleSidebarStateChange);
    
    // Also listen for resize in case sidebar width changes
    window.addEventListener('resize', measureSidebar);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('sidebarStateChanged', handleSidebarStateChange);
      window.removeEventListener('resize', measureSidebar);
    };
  }, []);

  // Handle video change
  const handleVideoChange = async (videoName) => {
    // Mark the new video as loaded
    setLoadedVideos(prev => new Set(prev).add(videoName));
    setSelectedVideo(videoName);
    setIsDropdownOpen(false);
    
    // Save the selected scene to Firestore
    if (user && videoName) {
      try {
        await saveSelectedScene(user.uid, videoName);
      } catch (error) {
        // Silently handle error - scene selection still works locally
      }
    }
  };

  // Get Vimeo embed URL for selected video
  const getVimeoUrl = (videoName) => {
    const video = availableVideos[videoName];
    if (!video) {
      return '';
    }
    if (!video.vimeoId || !video.vimeoHash) {
      return '';
    }
    
    // Extract numeric ID from vimeoId (handle cases where full URL was entered)
    let vimeoId = video.vimeoId.toString().trim();
    
    // If it's a full URL, extract just the numeric ID
    if (vimeoId.includes('vimeo.com')) {
      const match = vimeoId.match(/vimeo\.com\/(\d+)/);
      if (match && match[1]) {
        vimeoId = match[1];
      } else {
        return '';
      }
    }
    
    // Use responsive=1 and background=1 to ensure video fills container
    // dnt=1 disables tracking, transparent=0 ensures no padding
    const url = `https://player.vimeo.com/video/${vimeoId}?h=${video.vimeoHash}&autoplay=1&loop=1&muted=1&background=1&responsive=1&dnt=1`;
    return url;
  };

  // Load video backgrounds from Firestore
  useEffect(() => {
    if (!cachedRewardItems || cachedRewardItems.length === 0) return;

    const loadVideos = async () => {
      if (!user) return;

      try {
        // Filter video backgrounds from reward items
        const videoItems = cachedRewardItems.filter(item => item.itemType === 'video_background');
        
        if (videoItems.length === 0) {
          return;
        }

        // Get user's progress to determine unlocked videos
        const userProgress = await getUserProgress(user.uid);

        // Build available videos map and options list
        const videosMap = {};
        const allOptions = [];
        const unlockedOptions = [];

        videoItems.forEach(item => {
          const unlocked = isItemUnlocked(item.unlockRequirement, userProgress);
          
          videosMap[item.name] = {
            vimeoId: item.vimeoId,
            vimeoHash: item.vimeoHash,
            unlocked: unlocked
          };

          allOptions.push(item.name);
          if (unlocked) {
            unlockedOptions.push(item.name);
          }
        });

        setAvailableVideos(videosMap);
        setVideoBackgrounds(videoItems);
        setVideoOptions(unlockedOptions.sort()); // Only show unlocked videos in dropdown

        // Load saved scene preference from Firestore
        try {
          const userRewardItems = await getUserRewardItems(user.uid);
          const savedScene = userRewardItems?.selectedScene;
          const pacificSceneName = 'Pacific Northwest Coast';
          
          // Priority:
          // 1) Saved scene (if still unlocked)
          // 2) Pacific Northwest Coast (if unlocked)
          // 3) First unlocked video
          // 4) First video overall
          let initialVideo = null;
          if (savedScene && unlockedOptions.includes(savedScene)) {
            initialVideo = savedScene;
          } else if (unlockedOptions.includes(pacificSceneName)) {
            initialVideo = pacificSceneName;
          } else if (unlockedOptions.length > 0) {
            initialVideo = unlockedOptions[0];
          } else if (allOptions.length > 0) {
            // Fallback to first video even if locked (for display purposes)
            initialVideo = allOptions[0];
          }
          
          if (initialVideo) {
            setSelectedVideo(initialVideo);
            // Mark initial video as loaded
            setLoadedVideos(prev => new Set(prev).add(initialVideo));
          }
        } catch (error) {
          // Fallback to default if loading saved preference fails
          const pacificSceneName = 'Pacific Northwest Coast';
          let fallbackVideo = null;
          if (unlockedOptions.includes(pacificSceneName)) {
            fallbackVideo = pacificSceneName;
          } else if (unlockedOptions.length > 0) {
            fallbackVideo = unlockedOptions[0];
          } else if (allOptions.length > 0) {
            fallbackVideo = allOptions[0];
          }
          
          if (fallbackVideo) {
            setSelectedVideo(fallbackVideo);
            // Mark fallback video as loaded
            setLoadedVideos(prev => new Set(prev).add(fallbackVideo));
          }
        }
      } catch (error) {
        // Silently handle error - component will show empty state
      }
    };

    loadVideos();
  }, [cachedRewardItems, user]);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside scene dropdown
      if (sceneIconRef.current && !sceneIconRef.current.contains(event.target) &&
          sceneDropdownRef.current && !sceneDropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Full scene mode with video background
  // Position fixed: start at sidebar edge, span to viewport right edge
  // Using left + right (instead of width) is more reliable for edge-to-edge coverage
  return (
    <div 
      ref={sceneContainerRef}
      data-component="writing-tools-container"
      className={`flex flex-col ${className}`}
      style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: sidebarLeft,
        right: 0,
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
        transition: 'left 0.3s ease-in-out',
        width: 'auto', // Let left + right determine width
        zIndex: 40 // Below sidebar (z-50) but above content
      }}
    >
      {/* Header Bar */}
      <div 
        className="relative z-30 backdrop-blur-sm flex-shrink-0" 
        style={{ 
          margin: 0, 
          padding: 0, 
          width: '100%',
          backgroundColor: themeColors.header,
          borderBottom: `1px solid ${themeColors.divider}`
        }}
      >
        <div className="px-6 py-4 flex items-center justify-between" style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}>
          {/* Left side - Title */}
          <div className="flex items-center space-x-6">
            <h1 className="text-3xl font-bold font-poppins" style={{ color: themeColors.textPrimary }}>The Writers Room</h1>
          </div>

          {/* Center - Current Scene dropdown */}
          <div className="flex items-center space-x-3 flex-1 justify-center">
            <span className="text-lg" style={{ color: themeColors.textPrimary }}>Current Scene:</span>
            <div className="relative">
              <button
                ref={sceneIconRef}
                onClick={toggleDropdown}
                onKeyDown={(e) => { if (e.key === 'Escape') { setIsDropdownOpen(false); sceneIconRef.current?.focus(); } }}
                aria-expanded={isDropdownOpen}
                aria-controls="scene-dropdown"
                aria-label={`Current scene: ${selectedVideo || 'Loading'}`}
                className="flex items-center space-x-2 px-3 py-2 rounded transition-colors"
                style={{ 
                  color: themeColors.textPrimary,
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span className="text-lg underline">{selectedVideo || 'Loading...'}</span>
                <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu — disclosure widget; no role=listbox so no arrow-key keyboard contract */}
              {isDropdownOpen && (
                <div 
                  id="scene-dropdown"
                  aria-label="Scene options"
                  ref={sceneDropdownRef} 
                  className="absolute top-full left-0 mt-2 w-64 rounded-lg shadow-xl py-2 max-h-96 overflow-y-auto z-50"
                  style={{
                    backgroundColor: themeColors.card,
                    border: `1px solid ${themeColors.border}`
                  }}
                >
                  {videoOptions.map((videoName) => {
                    const video = availableVideos[videoName];
                    const isSelected = selectedVideo === videoName;
                    
                    return (
                      <button
                        key={videoName}
                        onClick={() => handleVideoChange(videoName)}
                        className="w-full text-left px-4 py-2 transition-colors duration-200"
                        style={{
                          color: themeColors.textPrimary,
                          backgroundColor: isSelected ? themeColors.borderSubtle : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = themeColors.borderSubtle;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {videoName}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Unlock message - directly to the right of dropdown */}
            <span className="text-sm" style={{ color: themeColors.textSecondary }}>Unlock more scenes as you complete levels.</span>
          </div>

          {/* Right side - empty for spacing */}
          <div className="w-0"></div>
        </div>
      </div>

      {/* Video Container - fills remaining space and spans full width */}
      <div className="flex-1 relative overflow-hidden" style={{ 
        margin: 0, 
        padding: 0, 
        width: '100%',
        minWidth: 0,
        position: 'relative',
        backgroundColor: 'transparent' // Ensure no background shows through
      }}>
        {/* Skip video when user prefers reduced motion (WCAG 2.3.3) */}
        {!prefersReducedMotion && selectedVideo && loadedVideos.has(selectedVideo) && (() => {
          const videoItem = videoBackgrounds.find(v => v.name === selectedVideo);
          if (!videoItem) return null;
          
          const videoUrl = getVimeoUrl(videoItem.name);
          if (!videoUrl) return null; // Skip if no valid URL
          
          return (
            <div
              key={videoItem.id}
              className="absolute transition-opacity duration-300 opacity-100 z-[5]"
              style={{ 
                margin: 0, 
                padding: 0,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                position: 'absolute',
                backgroundColor: 'transparent'
              }}
            >
              <iframe
                src={videoUrl}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  minWidth: '100%',
                  minHeight: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  margin: 0,
                  padding: 0,
                  border: 'none',
                  display: 'block',
                  position: 'absolute'
                }}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={`Writers Room Scene - ${videoItem.name}`}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default WritingTools;

